import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type CompanyDetail, BULK_MAX_ITEMS } from "@app/shared";
import { Link } from "react-router-dom";
import { companiesApi, type CompanyFilter } from "./api";
import { applicationsApi } from "../applications/api";
import { loadSelection, saveSelection, type SelectedCompany } from "../applications/bulkSelection";
import { Button, Card, Field, Input, PageTitle, Textarea } from "../../components/ui";
import { QueryView } from "../../components/states";
import { IndustryFilterSelect } from "./IndustryFilterSelect";
import { CompanyLocationMap } from "../../components/map/CompanyMap";
import { ApiError } from "../../lib/api";

// 企業の詳細（申込前に中身を確認する・F-05）。検索APIが返す CompanyDetail をそのまま表示する
// （連絡先・条件・地図まで既に取得済み）。ここから申込ダイアログへ進める。
function CompanyDetailDialog({
  company,
  onClose,
  onApply,
}: {
  company: CompanyDetail;
  onClose: () => void;
  onApply: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <Card className="max-h-[90vh] w-full max-w-2xl overflow-y-auto">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-extrabold text-brand-dark">{company.name}</h3>
            <p className="mt-1 text-xs text-slate-400">
              {company.area}・{company.industryLabel}・受入可能 {company.capacity}人
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-1">
          {company.jobTypes.map((j) => (
            <span
              key={j}
              className="rounded-pill bg-sky-light px-2.5 py-0.5 text-xs font-bold text-brand-dark"
            >
              {j}
            </span>
          ))}
        </div>

        <div className="mt-4 space-y-3 text-sm">
          <div>
            <h4 className="font-bold text-slate-500">仕事内容</h4>
            <p className="mt-1 whitespace-pre-wrap text-slate-700">{company.description}</p>
          </div>
          {company.conditions && (
            <div>
              <h4 className="font-bold text-slate-500">条件（服装・持ち物など）</h4>
              <p className="mt-1 whitespace-pre-wrap text-slate-700">{company.conditions}</p>
            </div>
          )}
          <div>
            <h4 className="font-bold text-slate-500">連絡先</h4>
            <p className="mt-1 text-slate-700">
              {company.contactName}
              {company.contactPhone ? ` / ${company.contactPhone}` : ""} / {company.contactEmail}
            </p>
          </div>
          {company.latitude != null && company.longitude != null && (
            <div>
              <h4 className="mb-1 font-bold text-slate-500">所在地</h4>
              <CompanyLocationMap latitude={company.latitude} longitude={company.longitude} />
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            閉じる
          </Button>
          <Button type="button" onClick={onApply}>
            この企業に申し込む
          </Button>
        </div>
      </Card>
    </div>
  );
}

interface ApplyForm {
  headcount: number;
  desiredJobType: string;
  startDate: string;
  endDate: string;
  note: string;
}

function ApplyDialog({ company, onClose }: { company: CompanyDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ApplyForm>({ defaultValues: { desiredJobType: company.jobTypes[0] ?? "" } });

  const mutation = useMutation({
    mutationFn: (v: ApplyForm) =>
      applicationsApi.create({
        companyId: company.id,
        headcount: Number(v.headcount),
        desiredJobType: v.desiredJobType,
        startDate: v.startDate,
        endDate: v.endDate,
        note: v.note ?? "",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      onClose();
    },
  });

  const apiErr = mutation.error instanceof ApiError ? mutation.error.message : null;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <Card className="w-full max-w-lg">
        <h3 className="text-lg font-extrabold text-brand-dark">{company.name} へ申し込む</h3>
        <p className="mt-1 text-xs text-slate-400">
          受入可能人数の目安: {company.capacity}人（最終判断は企業が行います）
        </p>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="人数" error={errors.headcount?.message}>
              <Input
                type="number"
                min={1}
                {...register("headcount", {
                  required: "人数は必須です",
                  min: { value: 1, message: "1以上" },
                })}
              />
            </Field>
            <Field label="希望職種" error={errors.desiredJobType?.message}>
              <Input {...register("desiredJobType", { required: "希望職種は必須です" })} />
            </Field>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="開始日" error={errors.startDate?.message}>
              <Input type="date" {...register("startDate", { required: "開始日は必須です" })} />
            </Field>
            <Field label="終了日" error={errors.endDate?.message}>
              <Input type="date" {...register("endDate", { required: "終了日は必須です" })} />
            </Field>
          </div>
          <Field label="連絡事項（任意）">
            <Textarea {...register("note")} placeholder="配慮事項など" />
          </Field>
          {apiErr && <p className="text-sm font-bold text-rose-500">{apiErr}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              閉じる
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              申し込む
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export function SchoolSearchPage() {
  const [filter, setFilter] = useState<CompanyFilter>({});
  const [draft, setDraft] = useState({ q: "", area: "", industryCode: "" });
  const [selected, setSelected] = useState<CompanyDetail | null>(null);
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  // 一括申込の選択（sessionStorage 保持・検索/ページ送り/リロードで消えない・§4.3）。
  const [picked, setPicked] = useState<SelectedCompany[]>(loadSelection);
  const isPicked = (id: string) => picked.some((p) => p.id === id);
  const togglePick = (c: CompanyDetail) => {
    if (!isPicked(c.id) && picked.length >= BULK_MAX_ITEMS) {
      alert(`一度に一括申込できるのは ${BULK_MAX_ITEMS} 社までです。`);
      return;
    }
    const next = isPicked(c.id)
      ? picked.filter((p) => p.id !== c.id)
      : [...picked, { id: c.id, name: c.name, jobTypes: c.jobTypes }];
    setPicked(next);
    saveSelection(next);
  };

  const query = useQuery({
    queryKey: ["companies", "search", filter],
    queryFn: () => companiesApi.search(filter),
  });

  return (
    <div className="space-y-6">
      <PageTitle title="企業を探す" subtitle="条件で絞り込み、受入先に申し込めます。" />
      <Card>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="キーワード"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            className="!w-56"
          />
          <Input
            placeholder="エリア"
            value={draft.area}
            onChange={(e) => setDraft({ ...draft, area: e.target.value })}
            className="!w-36"
          />
          <IndustryFilterSelect
            value={draft.industryCode}
            onChange={(code) => setDraft({ ...draft, industryCode: code })}
          />
          <Button onClick={() => setFilter({ ...draft, page: 1 })}>さがす</Button>
        </div>
      </Card>

      {picked.length > 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-2 bg-sky-soft">
          <span className="text-sm font-bold text-brand-dark">{picked.length} 社を選択中</span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setPicked([]);
                saveSelection([]);
              }}
            >
              選択解除
            </Button>
            <Link to="/app/school/bulk-apply">
              <Button>選択した企業に一括申込</Button>
            </Link>
          </div>
        </Card>
      )}

      <QueryView
        query={query}
        emptyWhen={(d) => d.items.length === 0}
        emptyMessage="条件に合う企業がありません。"
      >
        {(data) => (
          <div className="grid gap-4 md:grid-cols-2">
            {data.items.map((c) => (
              <Card key={c.id} className="flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-lg font-extrabold text-brand-dark">{c.name}</h3>
                  <label className="flex shrink-0 items-center gap-1 text-xs font-bold text-slate-500">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={isPicked(c.id)}
                      onChange={() => togglePick(c)}
                    />
                    一括に追加
                  </label>
                </div>
                <p className="mt-1 text-xs text-slate-400">
                  {c.area}・{c.industryLabel}・受入可能 {c.capacity}人
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {c.jobTypes.map((j) => (
                    <span
                      key={j}
                      className="rounded-pill bg-sky-light px-2.5 py-0.5 text-xs font-bold text-brand-dark"
                    >
                      {j}
                    </span>
                  ))}
                </div>
                <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-600">{c.description}</p>
                {c.conditions && (
                  <p className="mt-1 text-xs text-slate-400">条件: {c.conditions}</p>
                )}
                <div className="mt-3 flex gap-2">
                  <Button variant="ghost" onClick={() => setDetail(c)}>
                    詳細を見る
                  </Button>
                  <Button onClick={() => setSelected(c)}>この企業に申し込む</Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </QueryView>

      {detail && (
        <CompanyDetailDialog
          company={detail}
          onClose={() => setDetail(null)}
          onApply={() => {
            setSelected(detail);
            setDetail(null);
          }}
        />
      )}
      {selected && <ApplyDialog company={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
